import { useEffect, useMemo, useRef, useState } from "react";
import {
  CopyCodeButton,
  codeBlockClass,
  codeBlockContentClass,
  codeBlockHeaderClass,
} from "zudoku/ui/CodeBlock";
import { NativeSelect } from "zudoku/ui/NativeSelect";
import { HighlightedCode } from "zudoku/ui/SyntaxHighlight";
import type { PlaygroundOperation } from "../lib/oas";

// Code samples are generated by `@readme/httpsnippet` — ReadMe's own fork of
// httpsnippet, i.e. literally the engine that produced the samples on
// developer.movider.co before this site replaced it. That is the point: the
// team asked for the per-language *library* variants the old site had (PHP
// cURL vs Guzzle vs pecl_http, Node fetch vs Axios vs http, …), and
// hand-writing ~40 templates would be both a lot of code and a lot of ways
// to be subtly wrong. Recovered the old language list from a Wayback capture
// of the ReadMe page to confirm this is what it used.
//
// It is loaded lazily: ~37KB gzipped for all 22 targets, which is not worth
// putting in the initial bundle for a panel that only matters once someone
// scrolls to it. Until it resolves we render the cURL snippet, which is both
// the default selection and cheap to build by hand — so the common case
// paints immediately with no loading flash.

type Snippet = {
  key: string;
  title: string;
  clients: { key: string; title: string }[];
};

// `agent` is ReadMe's AI-agent prompt target and `json` just dumps the
// request as data — neither is a client library anyone integrates with.
const EXCLUDED_TARGETS = new Set(["agent", "json"]);

// Shown first, matching the quick-pick languages the old ReadMe page put in
// its picker (Shell / Node / Ruby / PHP / Python) plus Go, which this site
// added and Movider's own backend is written in.
const POPULAR = ["shell", "node", "php", "python", "ruby", "go"];

// httpsnippet target key → the language id Shiki highlights it as. Zudoku
// already ships (lazily) grammars for most of these; anything unmapped or
// unbundled falls back to plain text, which stays perfectly readable — it
// just loses colour. Not worth adding grammars to the bundle for the long
// tail.
const SHIKI_LANGUAGE: Record<string, string> = {
  shell: "bash",
  node: "javascript",
  javascript: "javascript",
  php: "php",
  python: "python",
  ruby: "ruby",
  go: "go",
  java: "java",
  csharp: "csharp",
  kotlin: "kotlin",
  swift: "swift",
  rust: "rust",
  powershell: "powershell",
  c: "c",
  objc: "objective-c",
  ocaml: "ocaml",
  clojure: "clojure",
  crystal: "crystal",
  r: "r",
  http: "http",
};

const STORAGE_KEY = "movider-code-sample-target";

/** Remembers the reader's language/client across pages and visits. */
function loadStoredTarget(): { target: string; client: string } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.target === "string" && typeof parsed?.client === "string") {
      return parsed;
    }
  } catch {
    // Private windows, blocked storage, or a stale/corrupt value — the
    // defaults below are always fine.
  }
  return null;
}

function storeTarget(target: string, client: string) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ target, client }));
  } catch {
    // Non-fatal: the selection just won't persist.
  }
}

function fieldValue(values: Record<string, string>, name: string) {
  return values[name]?.trim() || `<${name}>`;
}

/**
 * Describes the request as a HAR entry — the input format httpsnippet takes.
 * Path params are substituted, query params go in `queryString`, and form
 * fields become an urlencoded `postData` (every Movider operation that has a
 * body uses form-urlencoded, see lib/oas.ts).
 */
type HarRequest = {
  method: string;
  url: string;
  httpVersion: string;
  cookies: never[];
  headers: { name: string; value: string }[];
  queryString: { name: string; value: string }[];
  headersSize: number;
  bodySize: number;
  postData?: {
    mimeType: string;
    text?: string;
    params?: { name: string; value: string }[];
  };
};

function buildHarRequest(
  op: PlaygroundOperation,
  values: Record<string, string>,
  jsonBody?: string,
): HarRequest {
  let path = op.path;
  for (const field of op.fields) {
    if (field.in === "path") {
      path = path.replace(`{${field.name}}`, fieldValue(values, field.name));
    }
  }

  const queryString = op.fields
    .filter((f) => f.in === "query")
    .map((f) => ({ name: f.name, value: fieldValue(values, f.name) }));

  const formFields = op.fields.filter((f) => f.in === "formData");
  // A JSON-bodied operation carries the document the reader is editing, so
  // the sample shows exactly the request Send would make.
  const contentType = op.jsonBody?.contentType ?? "application/x-www-form-urlencoded";
  const hasBody = Boolean(op.jsonBody) || formFields.length > 0;

  return {
    method: op.method.toUpperCase(),
    url: `${op.server}${path}`,
    httpVersion: "HTTP/1.1",
    cookies: [],
    headers: hasBody ? [{ name: "Content-Type", value: contentType }] : [],
    queryString,
    headersSize: -1,
    bodySize: -1,
    ...(op.jsonBody
      ? {
          postData: {
            mimeType: op.jsonBody.contentType,
            text: jsonBody ?? op.jsonBody.example,
          },
        }
      : formFields.length
        ? {
            postData: {
              mimeType: "application/x-www-form-urlencoded",
              params: formFields.map((f) => ({
                name: f.name,
                value: fieldValue(values, f.name),
              })),
            },
          }
        : {}),
  };
}

/** The hand-built cURL fallback rendered until httpsnippet has loaded. */
function curlFallback(
  op: PlaygroundOperation,
  values: Record<string, string>,
  jsonBody?: string,
) {
  const har = buildHarRequest(op, values, jsonBody);
  const query = har.queryString
    .map((q) => `${encodeURIComponent(q.name)}=${encodeURIComponent(q.value)}`)
    .join("&");
  const data = har.postData?.text
    ? `  --data '${har.postData.text}'`
    : (har.postData?.params ?? [])
        .map((p) => `  --data-urlencode "${p.name}=${p.value}"`)
        .join(" \\\n");
  return [
    `curl --request ${har.method} \\`,
    `  --url "${har.url}${query ? `?${query}` : ""}"`,
    data,
  ]
    .filter(Boolean)
    .join(" \\\n");
}

export function CodeSamples({
  op,
  values,
  jsonBody,
}: {
  op: PlaygroundOperation;
  values: Record<string, string>;
  jsonBody?: string;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [snippet, setSnippet] = useState<{
    targets: Snippet[];
    convert: (har: object, target: string, client: string) => string | null;
  } | null>(null);
  const [target, setTarget] = useState("shell");
  const [client, setClient] = useState("curl");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { HTTPSnippet, availableTargets } = await import("@readme/httpsnippet");
      if (cancelled) return;

      const targets: Snippet[] = availableTargets()
        .filter((t) => !EXCLUDED_TARGETS.has(t.key))
        .map((t) => ({
          key: t.key,
          title: t.title,
          clients: t.clients.map((c) => ({ key: c.key, title: c.title })),
        }))
        .sort((a, b) => {
          const ai = POPULAR.indexOf(a.key);
          const bi = POPULAR.indexOf(b.key);
          if (ai !== -1 && bi !== -1) return ai - bi;
          if (ai !== -1) return -1;
          if (bi !== -1) return 1;
          return a.title.localeCompare(b.title);
        });

      setSnippet({
        targets,
        convert: (har, t, c) => {
          try {
            // Always an array, whose entries are `false` when that
            // target/client pair can't render this particular request.
            const instance = new HTTPSnippet(har as never);
            type ConvertArgs = Parameters<typeof instance.convert>;
            const [generated] = instance.convert(
              t as ConvertArgs[0],
              c as ConvertArgs[1],
            );
            return typeof generated === "string" ? generated : null;
          } catch {
            return null;
          }
        },
      });

      const stored = loadStoredTarget();
      if (stored && targets.some((t) => t.key === stored.target)) {
        setTarget(stored.target);
        setClient(stored.client);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeTarget = snippet?.targets.find((t) => t.key === target);
  const clients = activeTarget?.clients ?? [];

  const code = useMemo(() => {
    if (!snippet) return curlFallback(op, values, jsonBody);
    const generated = snippet.convert(
      buildHarRequest(op, values, jsonBody),
      target,
      client,
    );
    return generated ?? curlFallback(op, values, jsonBody);
  }, [snippet, op, values, jsonBody, target, client]);

  const selectTarget = (nextTarget: string) => {
    const next = snippet?.targets.find((t) => t.key === nextTarget);
    // Each language has its own client list, so the previous selection
    // ("curl" on Shell, say) is meaningless on the next one — reset to that
    // language's first client, which is httpsnippet's own default.
    const nextClient = next?.clients[0]?.key ?? "";
    setTarget(nextTarget);
    setClient(nextClient);
    storeTarget(nextTarget, nextClient);
  };

  const selectClient = (nextClient: string) => {
    setClient(nextClient);
    storeTarget(target, nextClient);
  };

  const popular = snippet?.targets.filter((t) => POPULAR.includes(t.key)) ?? [];
  const rest = snippet?.targets.filter((t) => !POPULAR.includes(t.key)) ?? [];

  return (
    <div className={codeBlockClass}>
      <div className={`${codeBlockHeaderClass} gap-1.5 px-1.5 py-1`}>
        <NativeSelect
          aria-label="Language"
          className="h-7 py-0 flex-1 min-w-0 text-xs"
          value={target}
          disabled={!snippet}
          onChange={(e) => selectTarget(e.target.value)}
        >
          {snippet ? (
            <>
              <optgroup label="Popular">
                {popular.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.title}
                  </option>
                ))}
              </optgroup>
              <optgroup label="All languages">
                {rest.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.title}
                  </option>
                ))}
              </optgroup>
            </>
          ) : (
            <option value="shell">Shell</option>
          )}
        </NativeSelect>

        {/* Only languages with more than one library get a second selector —
            showing a one-option dropdown next to Go or Python would be noise. */}
        {clients.length > 1 && (
          <NativeSelect
            aria-label="Library"
            className="h-7 py-0 flex-1 min-w-0 text-xs"
            value={client}
            onChange={(e) => selectClient(e.target.value)}
          >
            {clients.map((c) => (
              <option key={c.key} value={c.key}>
                {c.title}
              </option>
            ))}
          </NativeSelect>
        )}

        <CopyCodeButton contentRef={contentRef} />
      </div>
      <div ref={contentRef} className={codeBlockContentClass}>
        <HighlightedCode
          code={code}
          language={SHIKI_LANGUAGE[target] ?? "text"}
        />
      </div>
    </div>
  );
}
